import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { API_BASE } from "../config/api";
import "./membership.css";
import {
  applyMembership,
  generateMembershipCard,
  getMyMembership,
} from "../services/membershipService";

const Membership = () => {
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(1);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    rollNo: "",
    dob: "",
    gender: "",
    scholarType: "",
    department: "",
    customDepartment: "",
    year: "",
    previousMember: "",
    aboutYuva: "",
    expectedBenefits: "",
    address: "Saveetha Engineering College, Chennai",
  });

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  useEffect(() => {
    const loadMembership = async () => {
      try {
        const data = await getMyMembership();
        setMembership(data);
      } catch {
        setMembership(null);
      } finally {
        setLoading(false);
      }
    };

    loadMembership();
  }, []);

  const getStatusMeta = () => {
    if (!membership) {
      return {
        label: "Not Applied",
        className: "pending",
        icon: "⏳",
        message: "Begin your journey to YUVA membership"
      };
    }

    if (membership.status === "approved") {
      return {
        label: "Official Member",
        className: "approved",
        icon: "✓",
        message: "You are officially part of the YUVA ecosystem"
      };
    }

    if (membership.status === "rejected") {
      return {
        label: "Application Denied",
        className: "rejected",
        icon: "✕",
        message: "We appreciate your interest"
      };
    }

    return {
      label: "Pending Review",
      className: "pending",
      icon: "⏳",
      message: "Your application is being reviewed by our leadership"
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      department: formData.department === "Others" ? formData.customDepartment : formData.department
    };

    try {
      const data = await applyMembership(submitData);
      setMembership(data.membership);
      alert("Application submitted. Waiting for admin approval.");
    } catch (err) {
      alert(err.message || "Membership submission failed");
    }
  };

  const handleGenerateCard = async () => {
    try {
      const data = await generateMembershipCard();
      window.open(`${API_BASE}${data.cardUrl}`, "_blank");
    } catch (err) {
      alert(err.message || "Unable to generate card");
    }
  };

  const statusMeta = getStatusMeta();

  return (
    <>
      <Navbar />

      <div className="membership-page-premium">
        {/* ANIMATED BACKGROUND */}
        <div className="membership-background">
          <div className="bg-glow glow-1" />
          <div className="bg-glow glow-2" />
          <div className="bg-glow glow-3" />
        </div>

        {/* CINEMATIC HERO SECTION */}
        <section className="membership-hero">
          <div className="hero-content">
            <h2 className="hero-title">
              <span className="title-line">Your Identity</span>
              <span className="title-line gradient-text">Inside YUVA</span>
            </h2>
            <p className="hero-subtitle">
              Official access to leadership, innovation, and real-world impact ecosystems. Join an elite community of changemakers.
            </p>
            <div className="hero-accent-line" />
          </div>
        </section>

        <div className="membership-container">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner" />
              <p>Verifying your identity...</p>
            </div>
          ) : membership ? (
            // ===== APPROVED/PENDING/REJECTED STATE =====
            <section className="membership-status-section">
              <div className="status-container">
                {/* STATUS REVEAL */}
                <div className="status-reveal">
                  <div className={`status-icon ${statusMeta.className}`}>
                    {statusMeta.icon}
                  </div>
                  <h2 className="status-title">{statusMeta.label}</h2>
                  <p className="status-message">{statusMeta.message}</p>
                </div>

                {/* PREMIUM MEMBERSHIP CARD */}
                {membership.status === "approved" && (
                  <div className="premium-card-showcase">
                    <div className="card-container">
                      <div className="membership-card-premium">
                        <div className="card-background" />
                        <div className="card-glow-effect" />
                        <div className="card-shimmer" />

                        <div className="card-inner">
                          {/* CARD HEADER */}
                          <div className="card-header">
                            <div className="card-logo">
                              <div className="logo-mark">Y</div>
                            </div>
                            <div className="card-badge">
                              <span className="badge-icon">★</span>
                              <span>OFFICIAL</span>
                            </div>
                          </div>

                          {/* CARD CONTENT */}
                          <div className="card-content">
                            <div className="card-member-info">
                              <p className="card-label">Member ID</p>
                              <p className="card-id">{membership.membershipId}</p>
                            </div>

                            <div className="card-validity">
                              <div className="validity-item">
                                <p className="card-label">Valid Until</p>
                                <p className="card-value">
                                  {new Date(membership.validTill).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </p>
                              </div>
                              <div className="validity-item">
                                <p className="card-label">Status</p>
                                <p className="card-value verified">VERIFIED</p>
                              </div>
                            </div>

                            <div className="card-footer">
                              <p className="card-tagline">Elite Member of YUVA Ecosystem</p>
                              <div className="card-security">🔒 Secured</div>
                            </div>
                          </div>

                          {/* HOLOGRAPHIC ELEMENTS */}
                          <div className="holographic-accent accent-1" />
                          <div className="holographic-accent accent-2" />
                        </div>
                      </div>

                      {/* CARD REFLECTION */}
                      <div className="card-reflection" />
                    </div>

                    {/* CTA BUTTON */}
                    <button className="premium-button" onClick={handleGenerateCard}>
                      <span className="button-content">
                        <span className="button-icon">📥</span>
                        <span className="button-text">Access Digital Membership</span>
                      </span>
                      <span className="button-arrow">→</span>
                    </button>
                  </div>
                )}

                {/* PENDING STATE */}
                {membership.status === "pending" && (
                  <div className="pending-state">
                    <div className="pending-illustration">
                      <div className="pulse-ring" />
                      <div className="pulse-ring" style={{animationDelay: "0.5s"}} />
                      <div className="pulse-ring" style={{animationDelay: "1s"}} />
                    </div>
                    <p className="pending-text">
                      Your application is under review. We're carefully evaluating each candidate to maintain the excellence of our community.
                    </p>
                  </div>
                )}

                {/* REJECTED STATE */}
                {membership.status === "rejected" && (
                  <div className="rejected-state">
                    <p className="rejected-text">
                      Thank you for your interest in YUVA. We're always open to hearing from passionate individuals. Feel free to reach out if you'd like to learn more.
                    </p>
                  </div>
                )}
              </div>
            </section>
          ) : (
            // ===== APPLICATION FORM =====
            <section className="membership-form-section">
              <form onSubmit={handleSubmit} className="premium-form">
                {/* FORM PROGRESS INDICATOR */}
                <div className="form-progress">
                  <div className="progress-steps">
                    <div className={`step ${activeSection >= 1 ? 'active' : ''}`}>
                      <span className="step-number">1</span>
                      <span className="step-label">Personal</span>
                    </div>
                    <div className="progress-line" />
                    <div className={`step ${activeSection >= 2 ? 'active' : ''}`}>
                      <span className="step-number">2</span>
                      <span className="step-label">Academic</span>
                    </div>
                    <div className="progress-line" />
                    <div className={`step ${activeSection >= 3 ? 'active' : ''}`}>
                      <span className="step-number">3</span>
                      <span className="step-label">About YUVA</span>
                    </div>
                  </div>
                </div>

                {/* SECTION 1: PERSONAL DETAILS */}
                <div className={`form-section ${activeSection === 1 ? 'active' : ''}`}>
                  <div className="section-header">
                    <h3>Personal Details</h3>
                    <p>Help us know you better</p>
                  </div>

                  <div className="form-grid">
                    <input
                      name="name"
                      placeholder="Full Name *"
                      required
                      onChange={handleChange}
                      className="form-input"
                    />
                    <input
                      name="email"
                      type="email"
                      placeholder="Email Address *"
                      required
                      onChange={handleChange}
                      className="form-input"
                    />
                    <input
                      name="phone"
                      placeholder="Phone Number *"
                      required
                      onChange={handleChange}
                      className="form-input"
                    />
                    <input
                      name="rollNo"
                      placeholder="Register Number *"
                      required
                      onChange={handleChange}
                      className="form-input"
                    />
                    <input
                      name="dob"
                      type="date"
                      required
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Date of Birth*"
                    />
                    <select
                      name="gender"
                      required
                      onChange={handleChange}
                      className="form-input"
                    >
                      <option value="">Gender *</option>
                      <option>Male</option>
                      <option>Female</option>
                      <option>Other</option>
                    </select>
                  </div>

                  <button
                    type="button"
                    onClick={() => setActiveSection(2)}
                    className="section-nav-button next"
                  >
                    Continue →
                  </button>
                </div>

                {/* SECTION 2: ACADEMIC DETAILS */}
                <div className={`form-section ${activeSection === 2 ? 'active' : ''}`}>
                  <div className="section-header">
                    <h3>Academic Details</h3>
                    <p>Your academic background</p>
                  </div>

                  <div className="form-grid">
                    <select
                      name="scholarType"
                      required
                      onChange={handleChange}
                      className="form-input"
                    >
                      <option value="">Hosteller / Day Scholar *</option>
                      <option>Hosteller</option>
                      <option>Day Scholar</option>
                    </select>

                    <select
                      name="department"
                      required
                      onChange={handleChange}
                      className="form-input"
                    >
                      <option value="">Department *</option>
                      <option>Computer Science and Engineering</option>
                      <option>Information Technology</option>
                      <option>Electronics and Communication Engineering</option>
                      <option>Mechanical Engineering</option>
                      <option>Electrical and Electronics Engineering</option>
                      <option>Artificial Intelligence and Machine Learning</option>
                      <option>Artificial Intelligence and Data Science</option>
                      <option>Biomedical Engineering</option>
                      <option>Chemical Engineering</option>
                      <option>Civil Engineering</option>
                      <option>Management Studies</option>
                      <option>Agricultural Engineering</option>
                      <option>Others</option>
                    </select>

                    {formData.department === "Others" && (
                      <input
                        type="text"
                        name="customDepartment"
                        placeholder="Please specify your department *"
                        required
                        onChange={handleChange}
                        className="form-input"
                      />
                    )}

                    <select
                      name="year"
                      required
                      onChange={handleChange}
                      className="form-input"
                    >
                      <option value="">Year *</option>
                      <option>First Year</option>
                      <option>Second Year</option>
                      <option>Third Year</option>
                      <option>Final Year</option>
                    </select>
                  </div>

                  <div className="section-navigation">
                    <button
                      type="button"
                      onClick={() => setActiveSection(1)}
                      className="section-nav-button back"
                    >
                      ← Back
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveSection(3)}
                      className="section-nav-button next"
                    >
                      Continue →
                    </button>
                  </div>
                </div>

                {/* SECTION 3: ABOUT YUVA */}
                <div className={`form-section ${activeSection === 3 ? 'active' : ''}`}>
                  <div className="section-header">
                    <h3>About You & YUVA</h3>
                    <p>Tell us about your vision and goals</p>
                  </div>

                  <div className="form-grid single">
                    <select
                      name="previousMember"
                      required
                      onChange={handleChange}
                      className="form-input"
                    >
                      <option value="">Previous YUVA Member? *</option>
                      <option>Yes</option>
                      <option>No</option>
                    </select>

                    <textarea
                      name="aboutYuva"
                      placeholder="What do you know about YUVA? *"
                      required
                      onChange={handleChange}
                      className="form-textarea"
                    />

                    <textarea
                      name="expectedBenefits"
                      placeholder="What do you expect from this membership? *"
                      required
                      onChange={handleChange}
                      className="form-textarea"
                    />
                  </div>

                  <div className="section-navigation">
                    <button
                      type="button"
                      onClick={() => setActiveSection(2)}
                      className="section-nav-button back"
                    >
                      ← Back
                    </button>
                    <button
                      type="submit"
                      className="submit-button"
                    >
                      <span className="button-content">
                        <span className="button-icon">🚀</span>
                        <span className="button-text">Apply for Membership</span>
                      </span>
                      <span className="button-arrow">→</span>
                    </button>
                  </div>
                </div>
              </form>
            </section>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Membership;
